import { createApi, createCustomServiceCall } from '@thinknimble/tn-models'
import { axiosInstance } from '../axios-instance'
import { z } from 'zod'
import {
  organizationNumberShape,
  availableNumberShape,
  sendableNumberShape,
  sendableNumberSummaryShape,
} from './models'

/** List an organisation's own numbers with live status and rental price. */
const listForOrg = createCustomServiceCall({
  inputShape: { organization: z.string().uuid() },
  outputShape: z.array(z.object(organizationNumberShape)),
  cb: async ({ client, input, utils: { fromApi } }) => {
    const response = await client.get('/numbers/', {
      params: { organization: input.organization },
    })
    return fromApi(response.data)
  },
})

/** List an org's sendable numbers — chat read, gated to chat.access (no price). */
const listSendableForOrg = createCustomServiceCall({
  inputShape: { organization: z.string().uuid() },
  outputShape: z.array(z.object(sendableNumberShape)),
  cb: async ({ client, input, utils: { fromApi } }) => {
    const response = await client.get('/numbers/sendable/', {
      params: { organization: input.organization },
    })
    return fromApi(response.data)
  },
})

/** Per-org sendable-status flags for every org the caller belongs to (one read). */
const sendableSummary = createCustomServiceCall({
  outputShape: z.array(z.object(sendableNumberSummaryShape)),
  cb: async ({ client, utils: { fromApi } }) => {
    const response = await client.get('/numbers/sendable-summary/')
    return fromApi(response.data)
  },
})

/** Search Twilio for purchasable numbers in a country. */
const searchAvailable = createCustomServiceCall({
  inputShape: {
    organization: z.string().uuid(),
    country: z.string(),
    areaCode: z.string().optional(),
    contains: z.string().optional(),
  },
  outputShape: z.array(z.object(availableNumberShape)),
  cb: async ({ client, input, utils: { fromApi } }) => {
    const response = await client.get('/numbers/available/', {
      params: {
        organization: input.organization,
        country: input.country,
        area_code: input.areaCode,
        contains: input.contains,
      },
    })
    return fromApi(response.data)
  },
})

/** Subscribe to a chosen number — provisions and registers it server-side. */
const subscribe = createCustomServiceCall({
  inputShape: {
    organization: z.string().uuid(),
    phoneNumber: z.string(),
    label: z.string().optional(),
  },
  outputShape: {
    subscriptionId: z.string(),
    phoneNumber: z.string(),
    status: z.string(),
    twilioSid: z.string(),
  },
  cb: async ({ client, input, utils: { fromApi } }) => {
    const response = await client.post('/numbers/subscribe/', {
      organization: input.organization,
      phone_number: input.phoneNumber,
      label: input.label,
    })
    return fromApi(response.data)
  },
})

/** Cancel a number's rental subscription (suspends, then releases via webhook). */
const cancel = createCustomServiceCall({
  inputShape: {
    organization: z.string().uuid(),
    numberId: z.string().uuid(),
  },
  outputShape: organizationNumberShape,
  cb: async ({ client, input, utils: { fromApi } }) => {
    const response = await client.post(`/numbers/${input.numberId}/cancel/`, {
      organization: input.organization,
    })
    return fromApi(response.data)
  },
})

export const numberApi = createApi({
  client: axiosInstance,
  baseUri: '/numbers/',
  models: {
    entity: organizationNumberShape,
  },
  customCalls: {
    listForOrg,
    listSendableForOrg,
    sendableSummary,
    searchAvailable,
    subscribe,
    cancel,
  },
})
